import { useEffect, useState } from "react";
import { adminListEbookCategories, adminCreateEbookCategory, adminUpdateEbookCategory, adminReorderEbookCategories, adminDeleteEbookCategory } from "@/lib/api";
import { FolderTree, Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

// Admin panel to create / rename / reorder / hide / delete eBook categories.
// Category order here drives the shelf order on the public /books page; hiding a
// category hides its shelf and its eBooks from the public site.
export default function EbookCategoryManager({ onChange }) {
  const [cats, setCats] = useState([]);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => adminListEbookCategories().then((d) => { setCats(d.categories || []); onChange && onChange((d.categories || []).map((c) => c.name)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const n = newName.trim();
    if (!n) return;
    try { await adminCreateEbookCategory(n); setNewName(""); load(); toast("Category added"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const saveEdit = async (c) => {
    const n = editName.trim();
    if (!n) return;
    try { await adminUpdateEbookCategory(c.id, { name: n }); setEditId(null); load(); toast("Category renamed"); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const toggleHide = async (c) => { try { await adminUpdateEbookCategory(c.id, { hidden: !c.hidden }); load(); } catch (e) { toast.error(e.message); } };
  const move = async (i, d) => {
    const j = i + d; if (j < 0 || j >= cats.length) return;
    const arr = [...cats]; [arr[i], arr[j]] = [arr[j], arr[i]]; setCats(arr);
    try { await adminReorderEbookCategories(arr.map((c) => c.id)); } catch (e) { toast.error(e.message); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Its ${c.count} eBook(s) will become uncategorised (the eBooks are NOT deleted).`)) return;
    try { await adminDeleteEbookCategory(c.id); load(); toast("Category deleted"); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="mb-8 border border-line bg-surface p-5" data-testid="ebook-category-manager">
      <div className="mb-4 flex items-center gap-2.5"><FolderTree className="h-5 w-5 text-saffron" /><h2 className="font-serif-latin text-lg text-ink">Categories</h2></div>
      <p className="mb-4 text-sm text-ink-3">Organise eBooks into categories. Order here sets the shelf order on the /books page. Hiding a category hides its shelf and eBooks from the public site.</p>

      <div className="mb-4 flex items-center gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New category name…" data-testid="ebook-cat-new-input" className="flex-1 border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
        <button onClick={add} data-testid="ebook-cat-add-btn" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-3 py-2 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>

      <div className="space-y-2">
        {cats.map((c, i) => (
          <div key={c.id} className={`flex items-center gap-3 border border-line px-3 py-2 ${c.hidden ? "opacity-60" : ""}`} data-testid={`ebook-cat-row-${c.name}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} className="text-ink-3 hover:text-saffron"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button onClick={() => move(i, 1)} className="text-ink-3 hover:text-saffron"><ArrowDown className="h-3.5 w-3.5" /></button>
            </div>
            {editId === c.id ? (
              <input value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(c)} autoFocus className="flex-1 border border-line bg-parchment px-2 py-1 text-sm text-ink focus:border-saffron focus:outline-none" />
            ) : (
              <span className="flex-1 text-sm text-ink">{c.name} <span className="text-xs text-ink-3">· {c.count}</span></span>
            )}
            {editId === c.id ? (
              <button onClick={() => saveEdit(c)} className="text-saffron"><Check className="h-4 w-4" /></button>
            ) : (
              <button onClick={() => { setEditId(c.id); setEditName(c.name); }} title="Rename" className="text-ink-3 hover:text-saffron" data-testid={`ebook-cat-edit-${c.name}`}><Pencil className="h-4 w-4" /></button>
            )}
            <button onClick={() => toggleHide(c)} title={c.hidden ? "Show" : "Hide"} className="text-ink-3 hover:text-saffron" data-testid={`ebook-cat-hide-${c.name}`}>{c.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            <button onClick={() => remove(c)} title="Delete" className="text-ink-3 hover:text-red-600" data-testid={`ebook-cat-delete-${c.name}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {cats.length === 0 && <p className="py-4 text-center text-sm text-ink-3">No categories yet — add one above, or they appear automatically when you set a category on an eBook.</p>}
      </div>
    </div>
  );
}
